import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { redirectExternal } from "@/lib/redirect";
import { toast } from "sonner";
import {
  Fuel as FuelIcon, Plane, ShieldCheck, Check, ChevronRight, CalendarClock, Users, ArrowRight, Loader2,
  TrendingDown, Zap, Trophy,
} from "lucide-react";
import BackToCockpit from "@/components/BackToCockpit";

export default function Fuel() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialTail = (params.get("tail") || "").toUpperCase();
  const [plans, setPlans] = useState([]);
  const [tails, setTails] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!loading && !user) { nav("/login?next=/fuel"); return; }
    if (!user) return;
    const isOperator = user.role === "charter_operator";
    Promise.all([
      api.get("/fuel/plans"),
      api.get("/fuel/my-tails"),
      isOperator ? api.get("/operators/me/fleet").then((r) => r.data.fleet || []).catch(() => []) : Promise.resolve([]),
    ])
      .then(([p, t, f]) => { setPlans(p.data.plans || []); setTails(t.data.active || []); setFleet(f); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, [user, loading, nav]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <BackToCockpit />
      {/* Hero */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <FuelIcon className="h-4 w-4 text-sky" />
          <div className="mono text-[10px] tracking-[0.24em] text-sky/80 uppercase">// PJP · FUEL PROGRAM</div>
        </div>
        <h1
          className="font-display font-bold tracking-tight text-4xl sm:text-5xl lg:text-6xl mb-3"
          style={{ background: "linear-gradient(90deg,#4B9CFF,#7EC8FF,#A8DBFF)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent", textShadow: "0 0 40px rgba(75,156,255,0.35)" }}
        >
          Register a tail. Save on every uplift.
        </h1>
        <div className="text-white/60 text-base sm:text-lg max-w-2xl">
          One flat annual fee per aircraft unlocks PJP-negotiated fuel pricing at 48+ FBOs worldwide.
          <span className="text-avail font-semibold"> Break-even in one fill-up.</span>
        </div>
      </div>

      {/* Active tails */}
      {loaded && tails.length > 0 && (
        <div className="mb-8">
          <div className="mono text-[10px] tracking-[0.24em] text-white/60 uppercase mb-3">// YOUR ACTIVE TAILS</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {tails.map((t) => (
              <div key={t.id} className="card !p-4 border-avail/40" data-testid={`active-tail-${t.tail_number}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="h-11 w-11 rounded-xl bg-avail/15 border border-avail/40 grid place-items-center">
                      <Plane className="h-5 w-5 text-avail" />
                    </div>
                    <div>
                      <div className="font-display font-bold text-lg text-white mono">{t.tail_number}</div>
                      <div className="mono text-[10px] tracking-widest text-avail uppercase">{t.fuel_type === "jet_a" ? "JET A" : "100LL"} · ACTIVE</div>
                    </div>
                  </div>
                  <ShieldCheck className="h-5 w-5 text-avail flex-shrink-0" />
                </div>
                <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center justify-between text-xs">
                  <div className="text-white/60 flex items-center gap-1.5">
                    <CalendarClock className="h-3.5 w-3.5" />
                    Renews {new Date(t.expires_at).toLocaleDateString()}
                  </div>
                  <div className="mono text-[10px] text-white/50">{(t.authorized_user_ids || []).length}/2 users</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Plans */}
      <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-10">
        {plans.map((p) => (
          <PlanCard key={p.key} plan={p} fleet={fleet} initialTail={initialTail} activeTails={tails} />
        ))}
      </div>

      {/* How it works */}
      <div className="card !p-6 sm:!p-8 mb-8">
        <div className="mono text-[10px] tracking-[0.24em] text-sky/80 uppercase mb-3">// HOW IT WORKS</div>
        <div className="grid sm:grid-cols-3 gap-6">
          <Step n="1" icon={Plane} title="Register your tail" body="Enter your tail number and choose Jet A or 100LL. One-time annual fee." />
          <Step n="2" icon={TrendingDown} title="See PJP pricing" body="Once active, the tail unlocks volume-tiered pricing at every participating FBO." />
          <Step n="3" icon={Trophy} title="Best price wins" body="The lowest-priced FBO on each field earns the PJP FUEL badge — pilots see it in UNICOM." />
        </div>
      </div>

      {/* Guarantee stripe */}
      <div className="card !p-5 !border-avail/40 !bg-avail/[0.05]">
        <div className="flex items-start gap-3">
          <Zap className="h-5 w-5 text-avail flex-shrink-0 mt-1" />
          <div>
            <div className="font-display font-semibold text-white text-lg">Field-match guarantee</div>
            <div className="text-white/70 text-sm mt-1">
              Participating FBOs can opt into <span className="text-avail font-semibold">$0.10 below the cheapest advertised price on the field</span> — you always pay less than any walk-up.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ plan, fleet = [], initialTail = "", activeTails = [] }) {
  const [open, setOpen] = useState(false);
  // Auto-open the modal when landing with ?tail=… from the aircraft form.
  useEffect(() => {
    if (initialTail && plan.key === "jet_a") setOpen(true);
  }, [initialTail, plan.key]);
  return (
    <>
      <div className={`card !p-6 sm:!p-8 relative overflow-hidden ${plan.key === "jet_a" ? "border-sky/50" : ""}`}
           style={plan.key === "jet_a" ? { boxShadow: "0 0 24px rgba(75,156,255,0.2)" } : {}}>
        <div className="flex items-center justify-between mb-2">
          <div className="font-display font-bold text-3xl sm:text-4xl text-white tracking-tight">{plan.label}</div>
          {plan.key === "jet_a" && <span className="mono text-[9px] tracking-widest px-2 py-1 rounded-full bg-sky/20 border border-sky/50 text-sky">MOST POPULAR</span>}
        </div>
        <div className="flex items-baseline gap-2 mb-2">
          <span className="font-display font-bold text-4xl sm:text-5xl text-white">${(plan.annual_cents / 100).toFixed(0)}</span>
          <span className="text-white/50 text-sm">/ tail / year</span>
        </div>
        <div className="text-white/60 text-sm mb-5">Includes {plan.included_users} authorized users. Cancel anytime — no auto-renew.</div>
        <ul className="space-y-2 mb-6">
          {[
            "PJP-negotiated pricing at 48+ FBOs",
            "Volume-tier discounts (1-250, 251-500, 501-1000, 1001-2000, 2001+)",
            "Field-match guarantee at participating FBOs",
            `${plan.included_users} authorized pilots or ops managers`,
            "Includes fuel savings via UNICOM ordering",
          ].map((f) => (
            <li key={f} className="flex items-start gap-2 text-sm text-white/85">
              <Check className="h-4 w-4 text-avail flex-shrink-0 mt-0.5" />
              {f}
            </li>
          ))}
        </ul>
        <button
          onClick={() => setOpen(true)}
          data-testid={`btn-subscribe-${plan.key}`}
          className="w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full font-semibold text-sm tracking-tight transition-all hover:scale-[1.02] min-h-[52px]"
          style={{ background: "#4B9CFF", color: "#0A0F1A", boxShadow: "0 0 20px rgba(75,156,255,0.35)" }}
        >
          Register a Tail · ${(plan.annual_cents / 100).toFixed(0)}
          <ArrowRight className="h-4 w-4" />
        </button>
      </div>
      {open && <RegisterTailModal plan={plan} fleet={fleet} initialTail={initialTail} activeTails={activeTails} onClose={() => setOpen(false)} />}
    </>
  );
}

function Step({ n, icon: Icon, title, body }) {
  return (
    <div>
      <div className="mono text-[10px] tracking-[0.24em] text-sky/70 mb-2">// STEP {n}</div>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-sky" />
        <div className="font-display font-semibold text-white text-lg">{title}</div>
      </div>
      <div className="text-white/60 text-sm leading-relaxed">{body}</div>
    </div>
  );
}

function RegisterTailModal({ plan, fleet = [], initialTail = "", activeTails = [], onClose }) {
  const [tail, setTail] = useState(initialTail || "");
  const [type, setType] = useState("");
  const [emailA, setEmailA] = useState("");
  const [emailB, setEmailB] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [catalog, setCatalog] = useState({});
  useEffect(() => { api.get("/faa/type-ratings/catalog").then((r) => setCatalog(r.data || {})).catch(() => {}); }, []);

  // Fleet-picker helpers — hide tails already covered by an active PJP FUEL subscription.
  const alreadyPaid = new Set((activeTails || []).map((t) => (t.tail_number || "").toUpperCase()));
  const eligibleFleet = fleet.filter((ac) => !alreadyPaid.has((ac.tail_number || "").toUpperCase()));
  const hasFleet = eligibleFleet.length > 0;

  // If the deep-link tail matches an aircraft in the fleet, pre-fill its type too.
  useEffect(() => {
    if (!initialTail) return;
    const match = fleet.find((a) => (a.tail_number || "").toUpperCase() === initialTail.toUpperCase());
    if (match?.type_code) setType(match.type_code);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTail, fleet.length]);

  const pickFromFleet = (id) => {
    if (!id) { setTail(""); setType(""); return; }
    const ac = fleet.find((a) => a.id === id);
    if (!ac) return;
    setTail((ac.tail_number || "").toUpperCase());
    setType(ac.type_code || "");
  };

  const submit = async () => {
    if (!tail.trim() || tail.length < 2) { toast.error("Enter a valid tail number"); return; }
    setSubmitting(true);
    try {
      const emails = [emailA.trim(), emailB.trim()].filter(Boolean);
      const r = await api.post("/fuel/checkout", {
        tail_number: tail.trim().toUpperCase(),
        fuel_type: plan.key,
        aircraft_type: type.trim() || null,
        origin_url: window.location.origin,
        authorized_emails: emails,
      });
      const url = r?.data?.checkout_url;
      if (!url || !url.startsWith("http")) {
        // Defensive: never navigate to `undefined` — that produces a white screen.
        toast.error("Stripe didn't return a checkout URL. Please try again.");
        setSubmitting(false);
        return;
      }
      // Break out of any iframe wrapper — Stripe Checkout refuses to render in iframes.
      toast.success("Redirecting to Stripe checkout…");
      if (!redirectExternal(url)) {
        toast.error("Popup blocked. Please allow pop-ups and try again.");
        setSubmitting(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 overflow-y-auto" data-testid="register-tail-modal">
      <div className="bg-obsidian border border-white/15 rounded-t-2xl sm:rounded-2xl max-w-lg w-full max-h-[92vh] overflow-y-auto" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="sticky top-0 bg-obsidian/95 backdrop-blur border-b border-white/10 p-4 flex items-center gap-3">
          <Plane className="h-5 w-5 text-sky" />
          <div className="flex-1">
            <div className="font-display font-bold text-lg text-white">Register a Tail — {plan.label}</div>
            <div className="mono text-[10px] tracking-widest text-white/50 uppercase">${(plan.annual_cents / 100).toFixed(0)} / year</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-2 -mr-2" data-testid="close-register-modal">×</button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          {/* Fleet picker — only shown to operators who actually have a fleet. */}
          {hasFleet && (
            <div>
              <div className="eyebrow-muted mb-1.5 flex items-center gap-1.5"><Plane className="h-3.5 w-3.5" /> Pick from your fleet</div>
              <select
                data-testid="fleet-picker"
                className="input"
                value={fleet.find((a) => (a.tail_number || "").toUpperCase() === (tail || "").toUpperCase())?.id || ""}
                onChange={(e) => pickFromFleet(e.target.value)}
                style={{ fontSize: 16 }}
              >
                <option value="">— Type a new tail below —</option>
                {eligibleFleet.map((ac) => (
                  <option key={ac.id} value={ac.id}>
                    {ac.tail_number}{ac.type_code ? ` · ${ac.type_code}` : ""}{ac.home_base_icao ? ` · ${ac.home_base_icao}` : ""}
                  </option>
                ))}
              </select>
              <div className="mt-1 text-[11px] text-white/45">
                Or type a new tail below — it'll be added to your fleet automatically after payment.
              </div>
            </div>
          )}

          <div>
            <div className="eyebrow-muted mb-1.5">Tail number</div>
            <input value={tail} onChange={(e) => setTail(e.target.value.toUpperCase())} placeholder="N550AE" data-testid="input-tail" className="input mono text-lg tracking-widest" style={{ fontSize: 16 }} />
          </div>
          <div>
            <div className="eyebrow-muted mb-1.5">Aircraft type (optional)</div>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              data-testid="input-aircraft-type"
              className="input"
              style={{ fontSize: 16 }}
            >
              <option value="">Select aircraft…</option>
              {Object.entries(catalog)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([oem, list]) => (
                  <optgroup key={oem} label={oem}>
                    {[...list]
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
                  </optgroup>
                ))}
            </select>
          </div>
          <div>
            <div className="eyebrow-muted mb-1.5 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Authorized users (up to 2, optional)</div>
            <input value={emailA} onChange={(e) => setEmailA(e.target.value)} placeholder="pilot@example.com" data-testid="input-email-a" className="input mb-2" style={{ fontSize: 16 }} />
            <input value={emailB} onChange={(e) => setEmailB(e.target.value)} placeholder="mgr@example.com" data-testid="input-email-b" className="input" style={{ fontSize: 16 }} />
            <div className="mt-1 text-[11px] text-white/45">They must have PJP accounts. You (the payer) are already included.</div>
          </div>
          <div className="rounded-xl border border-sky/30 bg-sky/[0.06] p-3.5">
            <div className="mono text-[10px] tracking-widest text-sky uppercase mb-1">// WHAT YOU UNLOCK</div>
            <ul className="space-y-1.5 text-sm text-white/85">
              <li>• Volume-tier pricing at every participating FBO</li>
              <li>• PJP FUEL badge on the winning FBO at each field</li>
              <li>• 12 months coverage · <span className="text-avail">breaks even in one fill-up</span></li>
            </ul>
          </div>
        </div>

        <div className="sticky bottom-0 bg-obsidian/95 backdrop-blur border-t border-white/10 p-4 flex gap-2 justify-end" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
          <button onClick={onClose} className="btn" data-testid="btn-cancel-register">Cancel</button>
          <button onClick={submit} disabled={submitting} data-testid="btn-checkout" className="btn btn-primary">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {submitting ? "Redirecting…" : `Pay $${(plan.annual_cents / 100).toFixed(0)} via Stripe`}
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
